import * as ngCore from '@angular/core'

import * as components from './components'
import * as helpers from './helpers'

/**
 * A component tasked with monitoring the current section of a page where the user is located.
 *
 * **Slots**
 * - *default* placeholder for <atn-ds-stepper-item> components
 *
 * **Inputs**
 * - {@link AtnDsStepperComponent.connectedTo|connectedTo} Connected Html element that have scrollable content
 *
 * @example
 * ```html
 * <atn-ds-stepper [connectedTo]="body">
 *   <atn-ds-stepper-item></atn-ds-stepper-item>
 *   <atn-ds-stepper-item></atn-ds-stepper-item>
 * </atn-ds-stepper>
 * <div #body>scrollable content</div>
 * ```
 */
@ngCore.Component({
  encapsulation: ngCore.ViewEncapsulation.None,
  selector: 'atn-ds-stepper',
  standalone: true,
  styleUrls: ['./stepper.component.scss'],
  templateUrl: './stepper.component.html',
})
export class AtnDsStepperComponent implements ngCore.AfterContentInit {
  @ngCore.Input({ required: true })
  public connectedTo!: HTMLElement

  @ngCore.ContentChildren(components.item.Component)
  private set _itemsQueryList(
    itemsQueryList: ngCore.QueryList<components.item.Component>,
  ) {
    this._items = itemsQueryList.toArray()
  }

  private _items: components.item.Component[] = []

  ngAfterContentInit(): void {
    if (!this.connectedTo) {
      throw new Error('Connected Element is not provided for Stepper')
    }
    this.connectedTo.addEventListener('scroll', this._onScroll.bind(this))
  }

  /**
   * Find which section is currently active based on scroll position on the page
   */
  private _onScroll(): void {
    const { scrollHeight, scrollTop, clientHeight } = this.connectedTo
    // Find which step item is active based on scroll position
    const activeItemId =
      scrollHeight - scrollTop === clientHeight
        ? this._items[this._items.length - 1].id
        : this._items.find(
            item =>
              (document.querySelector<HTMLElement>(
                helpers.getStepperItemQuerySelector(item.id),
              )?.offsetTop ?? 0) >= scrollTop,
          )?.id
    /**
     * Set {@link components.item.Component.isActive|isActive} property for each step item
     */
    this._items.forEach(item => (item.isActive = item.id === activeItemId))
  }
}
